// OtpModal.js
import React, { useRef } from "react";
import {
    Box,
    Button,
    IconButton,
    InputAdornment,
    Modal,
    TextField,
    Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { toast } from "react-toastify";

const ForgotPasswordModal = ({
    isModalFgtOpen,
    handleCloseFgtModal,
    email,
    otp,
    setOtp,
    generatedFgtOtp,
    isOtpVerified,
    setIsOtpVerified,
    isOtpMismatch,
    setIsOtpMismatch,
    newPassword,
    setNewPassword,
    confirmPassword,
    setConfirmPassword,
    passwordError,
    checkPasswordMatch,
    handleCreateNewPwd,
    setVerifiedMobileNumber,
    number,
    setIsOtpSent,
}) => {
    const newPasswordRef = useRef(null);
    const [showNewPassword, setShowNewPassword] = React.useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

    const handleVerifyOtp = (newOtp) => {
        if (!newOtp) {
            return;
        }

        const enteredOtp = newOtp.join("").trim();

        if (String(enteredOtp) === String(generatedFgtOtp)) {
            toast.success("OTP verified successfully.");
            setIsOtpVerified(true);
            setIsOtpMismatch(false);
            setVerifiedMobileNumber(number);

            setIsOtpSent(false);

            setTimeout(() => {
                if (newPasswordRef.current) {
                    newPasswordRef.current.focus();
                }
            }, 100);
        } else {
            toast.error("Invalid OTP. Please try again.");
            setIsOtpVerified(false);
            setIsOtpMismatch(true);
        }
    };

    return (
        <Modal open={isModalFgtOpen} onClose={handleCloseFgtModal}>
            <Box
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    padding: 4,
                    backgroundColor: "#ffffff",
                    borderRadius: 2,
                    width: 400,
                    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                }}
            >
                <Typography
                    variant="h6"
                    align="center"
                    sx={{ fontSize: "1.5rem", fontWeight: "600", color: "#333" }}
                >
                    Validate Mobile Number
                </Typography>
                <Typography
                    variant="body2"
                    align="center"
                    sx={{ marginBottom: 2, color: "#555" }}
                >
                    An email has been sent to <strong>{email}</strong>. Please enter the OTP below:
                </Typography>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        marginTop: 2,
                    }}
                >
                    {otp.map((digit, index) => (
                        <TextField
                            key={index}
                            type="text"
                            variant="outlined"
                            placeholder="0"
                            value={digit}
                            onChange={(e) => {
                                const newOtp = [...otp];
                                newOtp[index] = e.target.value;


                                const enteredOtp = newOtp.join("").trim();
                                const isMismatch =
                                    enteredOtp.length > 0 &&
                                    enteredOtp !== generatedFgtOtp.slice(0, enteredOtp.length);
                                setIsOtpMismatch(isMismatch);
                                setIsOtpVerified(enteredOtp === generatedFgtOtp);

                                if (e.target.value && index < otp.length - 1) {
                                    document.getElementById(`otp-${index + 1}`).focus();
                                }

                                setOtp(newOtp);

                                if (newOtp.every((d) => d.length === 1)) {
                                    handleVerifyOtp(newOtp);
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Backspace" && !digit) {
                                    if (index > 0) {
                                        const newOtp = [...otp];
                                        newOtp[index - 1] = "";
                                        setOtp(newOtp);
                                        document.getElementById(`otp-${index - 1}`).focus();
                                    }
                                }
                            }}
                            id={`otp-${index}`}
                            sx={{
                                marginRight: 1,
                                width: "50px",
                                height: "50px",
                                backgroundColor: "#f5f5f5",
                                borderRadius: "8px",
                                "&:hover": {
                                    backgroundColor: "#e0e0e0",
                                },
                                "&.Mui-focused": {
                                    borderColor: "#3f51b5",
                                },
                            }}
                            InputProps={{
                                sx: {
                                    padding: "0",
                                    height: "100%",
                                    "& input": {
                                        textAlign: "center",
                                        padding: "0",
                                    },
                                },
                            }}
                        />
                    ))}
                    {isOtpVerified && (
                        <CheckCircleIcon sx={{ color: "green", marginLeft: 1 }} />
                    )}

                    {!isOtpVerified &&
                        isOtpMismatch &&
                        otp.some((digit) => digit.length > 0) && (
                            <CancelIcon sx={{ color: "red", marginLeft: 1 }} />
                        )}
                </Box>
                <TextField
                    type={showNewPassword ? "text" : "password"}
                    variant="outlined"
                    placeholder="New Password"
                    fullWidth
                    value={newPassword}
                    onChange={(e) => {
                        setNewPassword(e.target.value);
                        checkPasswordMatch(e.target.value)
                    }
                    }
                    inputRef={newPasswordRef}
                    sx={{ marginTop: 2 }}
                    disabled={!isOtpVerified}

                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                                    {showNewPassword ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                <TextField
                    type={showConfirmPassword ? "text" : "password"}
                    variant="outlined"
                    placeholder="Confirm Password"
                    fullWidth
                    value={confirmPassword}
                    disabled={!isOtpVerified}
                    onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        checkPasswordMatch(e.target.value);
                    }}
                    sx={{ marginTop: 2 }}

                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                                    {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                {passwordError && (
                    <Typography variant="body2" color="error" sx={{ marginTop: 1 }}>
                        {passwordError}
                    </Typography>
                )}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginTop: 2,
                    }}
                >
                    <Button
                        onClick={handleCreateNewPwd}
                        variant="contained"
                        sx={{
                            flex: 1,
                            marginRight: 1,
                            "&:hover": { backgroundColor: "#303f9f" },
                        }}
                    >
                        Create New Password
                    </Button>
                    <Button
                        // onClick={handleCloseModal}
                        // onClick={() => {
                        //     handleCloseModal(); // Close the forgot password modal
                        //     setIsOtpVerified(false); // Reset OTP verification state
                        //     setOtp(Array(4).fill("")); // Reset OTP input
                        //   }}
                        onClick={handleCloseFgtModal}
                        variant="outlined"
                        sx={{
                            flex: 1,
                            marginLeft: 1,
                            borderColor: "#3f51b5",
                            color: "#3f51b5",
                            "&:hover": { borderColor: "#303f9f", color: "#303f9f" },
                        }}
                    >
                        Back to Login
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default ForgotPasswordModal;